import * as React from 'react';
import { ITripGroup } from '../types/trip.types';
import { getStatusLabel } from '../constants/trip-status';

interface TripPhaseTabsProps {
    tripGroups: ITripGroup[];
    selectedTripIndex: number;
    viewType: 'trip' | 'overall';
    allTripsApproved: boolean;
    resolvedCount: number;
    onTripSelect: (index: number, tripGroup: ITripGroup) => Promise<void>;
    onFinalizeSelect?: () => void;
}

const VISIBLE_TRIP_ROWS = 5;
const TRIP_ROW_HEIGHT = 36;

const statusColor = (status: string): string => {
    if (status === 'pending') return '#6c757d';
    if (status === 'in_review') return '#0d6efd';
    if (status === 'approved') return '#198754';
    if (status === 'declined') return '#dc3545';
    return '#fd7e14';
};

export const TripPhaseTabs = (props: TripPhaseTabsProps) => {
    const tripCount = props.tripGroups.length;
    if (tripCount === 0) return null;

    const finalizeActive = props.viewType === 'overall';
    const listScrolls = tripCount > VISIBLE_TRIP_ROWS;
    const ready = props.allTripsApproved;

    return (
        <div style={{
            background: '#fff',
            borderRadius: 6,
            marginBottom: 16,
            border: '1px solid #e1e8ed',
            flexShrink: 0,
            minHeight: 'min-content',
        }}>
            <div style={{
                padding: '7px 14px',
                borderBottom: '1px solid #eef2f7',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#64748b',
            }}>
                Trips ({tripCount})
            </div>
            <div
                role='list'
                aria-label='Trips'
                style={listScrolls ? {
                    maxHeight: VISIBLE_TRIP_ROWS * TRIP_ROW_HEIGHT,
                    overflowY: 'auto',
                    flexShrink: 0,
                } : {
                    flexShrink: 0,
                }}
            >
                {props.tripGroups.map((tripGroup, index) => {
                    const isActive = index === props.selectedTripIndex && props.viewType === 'trip';
                    return (
                        <div
                            key={`trip-${tripGroup.id}-${index}`}
                            role='listitem'
                            onClick={async () => {
                                await props.onTripSelect(index, tripGroup);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                minHeight: TRIP_ROW_HEIGHT,
                                height: TRIP_ROW_HEIGHT,
                                padding: '0 14px',
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                                borderBottom: index < tripCount - 1 ? '1px solid #f1f5f9' : 'none',
                                background: isActive ? '#eff6ff' : '#fff',
                                boxShadow: isActive ? 'inset 3px 0 0 #0d6efd' : 'none',
                                minWidth: 0,
                            }}
                        >
                            <div style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 700,
                                background: isActive ? '#0d6efd' : '#e2e8f0',
                                color: isActive ? '#fff' : '#334155',
                                flexShrink: 0,
                            }}>
                                {index + 1}
                            </div>
                            <div style={{
                                flex: '1 1 88px',
                                minWidth: 0,
                                fontSize: 16,
                                fontWeight: isActive ? 700 : 600,
                                color: '#0f172a',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                {tripGroup.name}
                            </div>
                            <div style={{
                                flex: '0 1 auto',
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#334155',
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                                letterSpacing: '0.01em',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                {tripGroup.startDate}
                                <span style={{ color: '#64748b', margin: '0 5px', fontWeight: 500 }}>–</span>
                                {tripGroup.endDate}
                            </div>
                            <span style={{
                                flexShrink: 0,
                                marginLeft: 'auto',
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textTransform: 'uppercase',
                                background: statusColor(tripGroup.status),
                                color: '#fff',
                            }}>
                                {getStatusLabel(tripGroup.status)}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div
                role={ready ? 'button' : undefined}
                tabIndex={ready ? 0 : undefined}
                onClick={() => {
                    if (ready && props.onFinalizeSelect) {
                        props.onFinalizeSelect();
                    }
                }}
                onKeyDown={(e) => {
                    if (!ready || !props.onFinalizeSelect) {
                        return;
                    }

                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        props.onFinalizeSelect();
                    }
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '7px 14px',
                    borderTop: '1px solid #e2e8f0',
                    background: ready || finalizeActive ? '#f0fdf4' : '#f8fafc',
                    lineHeight: 1.3,
                    cursor: ready ? 'pointer' : 'default',
                    boxShadow: finalizeActive ? 'inset 3px 0 0 #198754' : 'none',
                    flexShrink: 0,
                }}
            >
                <div style={{
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '4px 8px',
                    fontSize: 14,
                }}>
                    <span style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: ready || finalizeActive ? '#166534' : '#1e293b',
                    }}>
                        {ready || finalizeActive ? 'Ready for review & finalize' : 'Review & finalize'}
                    </span>
                    <span style={{
                        color: ready || finalizeActive ? '#15803d' : '#475569',
                        fontWeight: 500,
                    }}>
                        {props.resolvedCount} of {tripCount} trips
                    </span>
                </div>
                <span style={{
                    flexShrink: 0,
                    padding: '4px 9px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    background: ready || finalizeActive ? '#198754' : '#e2e8f0',
                    color: ready || finalizeActive ? '#fff' : '#475569',
                }}>
                    {ready || finalizeActive ? 'Ready' : 'In progress'}
                </span>
            </div>
        </div>
    );
};
